import HttpRequest from "@/util/HttpRequest";
import constant from "@/constant/constant";
import { cached } from "@/util/RequestCache";

// 회사 목록을 보관하는 시간. 회사가 새로 등록될 수 있어 무기한으로 두지는 않는다.
const COMPANY_LIST_TTL_MS = 60 * 1000;

export default {
  /**
   * 회사 목록
   * @params {page, perPage}
   *
   * 화면을 옮길 때마다 같은 목록을 다시 받아오던 것을 캐시로 막는다.
   * 조회 조건(페이지·검색어·현장 등)이 그대로 질의 문자열이 되므로 키에도 전부 넣는다.
   * 다만 회사가 새로 등록되면 목록이 달라지므로, 단일 항목 조회와 달리
   * 세션 내내 붙들지 않고 COMPANY_LIST_TTL_MS 동안만 보관한다.
   */
  async LOAD_COMPANY_LIST({ commit }, params) {
    if (!params.projectId) return;

    const option = { params };

    // axios 는 undefined / null 인 조건을 아예 보내지 않는다. 키도 같은 기준으로 만든다.
    const key = Object.keys(params)
      .filter((k) => params[k] !== undefined && params[k] !== null)
      .sort()
      .map((k) => `${k}=${params[k]}`)
      .join("&");

    const response = await cached(
      `companyList:${key}`,
      () => HttpRequest.get("/api/v1/company", option),
      COMPANY_LIST_TTL_MS
    );

    // mutations 메소드 호출
    commit("LOAD_COMPANY_LIST", response);
    return response;
  },

  /**
   * 회사 목록
   * @params {page, perPage}
   */
  async LOAD_COMPANY_TREE({ commit }, params) {
    if (!params.projectId) return;

    const option = {
      params: {
        projectId: params.projectId,
      },
    };

    const subCompanies = localStorage.getItem("sub_company");
    if (subCompanies && !params.new) {
      try {
        const companies = JSON.parse(subCompanies);
        commit("LOAD_COMPANY_TREE", companies);
        return companies;
      } catch (e) {
        return undefined;
      }
    } else {
      return new Promise((resolve, reject) => {
        HttpRequest.get("/api/v1/company/tree", option)
          .then((response) => {
            localStorage.setItem("sub_company", JSON.stringify(response));

            // mutations 메소드 호출
            commit("LOAD_COMPANY_TREE", response);
            resolve(response);
          })
          .catch((err) => reject(err));
      });
    }
  },

  /**
   * 회사 정보. companyInfo 는 마지막 조회 결과 하나만 담는 단일 슬롯이라,
   * 서로 다른 회사를 번갈아 조회하면(작성자 회사 → 작성자 원글 회사) 매번
   * 서로를 밀어내 재조회가 일어났다. 캐시는 id 별로 잡고, commit 은 항상 해서
   * companyInfo 가 방금 요청한 회사를 가리키도록 한다.
   */
  async LOAD_COMPANY({ commit }, params) {
    const companyId = params.companyId;

    const response = await cached(`company:${companyId}`, () => HttpRequest.get(`/api/v1/company/${companyId}`));

    // mutations 메소드 호출
    commit("LOAD_COMPANY", response);
    return response;
  },

  async LOAD_COMPANY_FIELD({ commit }, params) {
    const companyId = params.companyId;

    const option = {
      params: {
        page: params.page,
        perPage: params.perPage,
        fieldName: params.fieldName,
        projectId: params.projectId,
        fieldType: params.fieldType,
        fieldCode: params.fieldCode,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get(`/api/v1/company/field/${companyId}`, option)
        .then((response) => {
          // mutations 메소드 호출
          commit("LOAD_COMPANY_FIELD", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },

  async LOAD_MEMBER_LIST({ commit }, params) {
    const option = {
      params: {
        page: params.page,
        perPage: params.perPage,
        company_id: params.company_id,
      },
    };

    return new Promise((resolve, reject) => {
      HttpRequest.get("/api/v1/company/member", option)
        .then((response) => {
          // mutations 메소드 호출
          commit("LOAD_MEMBER_LIST", response);
          resolve(response);
        })
        .catch((err) => reject(err));
    });
  },
};
